import { secondsInDay } from "@theprelude/core";
import {
  ITEM_ROOT,
  ODataQuery,
  PartnerEndpointQueryKey,
} from "@theprelude/sdk";
import { Filter, OdataOperator, OdataQuantifier } from "@/lib/filter";
import {
  getOdataDurationString,
  isDurationId,
  stripDurationId,
} from "./available-filters";

export type ODataMethod = Exclude<
  OdataOperator,
  "is" | "is not" | "less than" | "greater than"
>;

export const OPERATOR_ODATA_METHOD: Record<ODataMethod, string> = {
  contains: "contains",
  "does not contain": "contains",
  matches: "matchespattern",
  "does not match": "matchespattern",
};

export const NEGATED_OPERATOR_ODATA_METHOD = new Set<ODataMethod>([
  "does not contain",
  "does not match",
]);

type IsArray<T> = T extends unknown[] ? T : never;
type IsNotArray<T> = T extends unknown[] ? never : T;

type ODataFilter<T> = NonNullable<ODataQuery<T>["filter"]>;
type ODataArrayFilter<T> = IsArray<ODataFilter<T>>;
type ODataObjectFilter<T> = IsNotArray<ODataFilter<T>>;

function isNull(filter: { value: unknown; operator: OdataOperator }) {
  return filter.value === null && filter.operator === "is";
}

function checkStartNullFilters(filter: Filter) {
  const actualId = isDurationId(filter.id)
    ? stripDurationId(filter.id)
    : filter.id;
  return (
    actualId === "start_time" &&
    filter.with?.event &&
    filter.scope === "failures" &&
    (isNull(filter) || (filter.ors && filter.ors.some((or) => isNull(or))))
  );
}

function checkNullInstanceFilters(filter: Filter) {
  return isNull(filter) && filter.with?.control;
}

function checkAnyDurationEqualityFilters(filter: Filter) {
  const isDurationEquality = (op: string, val: any) => {
    return (
      (op === "is" || op === "is not") &&
      typeof val === "object" &&
      val !== null &&
      "value" in val &&
      typeof val.value === "string" &&
      val.value.includes("DT0H0M0S'")
    );
  };

  const mainIsDurationEquality = isDurationEquality(
    filter.operator,
    filter.value,
  );

  const hasOrDurationEquality =
    filter.ors &&
    filter.ors.some((or) => isDurationEquality(or.operator, or.value));

  return mainIsDurationEquality || hasOrDurationEquality;
}

function createDurationRangeCondition(
  operator: string,
  value: { type: "raw"; value: string },
  actualId: string,
) {
  const durationString = value.value;
  const daysMatch = durationString.match(/P(\d+)DT0H0M0S/);
  if (!daysMatch) {
    throw new Error(`Invalid duration format: ${durationString}`);
  }

  const days = Number(daysMatch[1]);
  const startSeconds = days * secondsInDay;
  const endSeconds = (days + 1) * secondsInDay;

  const startDuration = {
    type: "raw" as const,
    value: `now() sub duration'${getOdataDurationString(startSeconds)}'`,
  };
  const endDuration = {
    type: "raw" as const,
    value: `now() sub duration'${getOdataDurationString(endSeconds)}'`,
  };

  const rangeCondition = {
    and: [
      { [actualId]: { lt: startDuration } },
      { [actualId]: { ge: endDuration } },
    ],
  };

  return operator === "is not" ? { not: [rangeCondition] } : rangeCondition;
}

function getUnifiedDurationEqualityOdataFilter<T>(
  filter: Filter,
): ODataObjectFilter<T> {
  const actualId = stripDurationId(filter.id);
  const { scope, quantifier = "any", with: withs = {} } = filter;

  const isDurationEquality = (op: string, val: any) => {
    return (
      (op === "is" || op === "is not") &&
      typeof val === "object" &&
      val !== null &&
      "value" in val &&
      typeof val.value === "string" &&
      val.value.includes("DT0H0M0S'")
    );
  };

  if (filter.ors && filter.ors.length > 0) {
    let mainCondition;
    if (isDurationEquality(filter.operator, filter.value)) {
      mainCondition = createDurationRangeCondition(
        filter.operator,
        filter.value as { type: "raw"; value: string },
        actualId,
      );
    } else {
      const comparator = getOperatorComparator(filter);
      mainCondition = { [actualId]: comparator };
    }

    const orConditions = filter.ors.map((or) => {
      if (isDurationEquality(or.operator, or.value)) {
        return createDurationRangeCondition(
          or.operator,
          or.value as { type: "raw"; value: string },
          actualId,
        );
      } else {
        const comparator = getOperatorComparator({
          ...filter,
          operator: or.operator,
          value: or.value,
        });
        return { [actualId]: comparator };
      }
    });

    const finalCondition = {
      or: [mainCondition, ...orConditions],
    };

    if (scope) {
      return {
        [scope]: {
          [quantifier]: {
            and: [withs, finalCondition],
          },
        },
      } as ODataObjectFilter<T>;
    }

    return finalCondition as ODataObjectFilter<T>;
  } else {
    const finalCondition = createDurationRangeCondition(
      filter.operator,
      filter.value as { type: "raw"; value: string },
      actualId,
    );

    if (scope) {
      return {
        [scope]: {
          [quantifier]: {
            and: [withs, finalCondition],
          },
        },
      } as ODataObjectFilter<T>;
    }

    return finalCondition as ODataObjectFilter<T>;
  }
}

export function getOdataFilters<T>(
  filters: Filter[],
  withSpecialOverrides = true,
): ODataArrayFilter<T> {
  const nullInstanceFilters = filters
    .filter((filter) => checkNullInstanceFilters(filter))
    .map((filter) => {
      return getNullInstanceFilter(filter);
    });

  const nullStartTimeFilters = filters
    .filter((filter) => checkStartNullFilters(filter))
    .map((filter) => {
      return getNullStartTimeFilter(filter);
    });

  const hasNullFilters =
    nullInstanceFilters.length > 0 || nullStartTimeFilters.length > 0;

  if (hasNullFilters && withSpecialOverrides) {
    return [
      {
        or: [
          ...nullInstanceFilters,
          ...nullStartTimeFilters,
          {
            and: filters.map((filter) => {
              if (checkStartNullFilters(filter)) {
                return getNonNullOdataFilter(filter);
              }
              if (checkAnyDurationEqualityFilters(filter)) {
                return getUnifiedDurationEqualityOdataFilter(filter);
              }
              return getOdataFilter(filter);
            }),
          },
        ],
      },
    ];
  }

  return filters.map((filter) => {
    if (checkAnyDurationEqualityFilters(filter) && withSpecialOverrides) {
      return getUnifiedDurationEqualityOdataFilter(filter);
    }
    return getOdataFilter(filter);
  });
}
function getNonNullOdataFilter<T>(filter: Filter): ODataObjectFilter<T> {
  const allConditions = [];

  if (!isNull(filter)) {
    allConditions.push({
      value: filter.value,
      operator: filter.operator,
    });
  }

  if (filter.ors) {
    const nonNullOrs = filter.ors.filter((or) => !isNull(or));
    allConditions.push(...nonNullOrs);
  }

  if (allConditions.length > 0) {
    const firstCondition = allConditions[0];
    if (firstCondition) {
      const modifiedFilter = {
        ...filter,
        value: firstCondition.value,
        operator: firstCondition.operator,
        ors: allConditions.length > 1 ? allConditions.slice(1) : undefined,
      };

      if (checkAnyDurationEqualityFilters(modifiedFilter)) {
        return getUnifiedDurationEqualityOdataFilter(modifiedFilter);
      }

      return getOdataFilter(modifiedFilter);
    }
  }

  return {} as ODataObjectFilter<T>;
}

function getNullInstanceFilter(filter: Filter) {
  return {
    not: [{ controls: { any: { [ITEM_ROOT]: { eq: filter.with?.control } } } }],
  };
}

function getNullStartTimeFilter(filter: Filter) {
  return {
    failures: { all: { event: { ne: filter.with?.event } } },
  };
}

function getOdataFilter<T>(filter: Filter): ODataObjectFilter<T> {
  const {
    value,
    scope,
    quantifier = "any",
    with: withs = {},
    isArray,
  } = filter;

  if (scope === "excepted" && value === false) {
    return {
      not: [
        {
          [scope]: {
            [quantifier]: {
              and: [withs, getOperatorFilter({ ...filter, value: true })],
            },
          },
        },
      ],
    };
  }

  if (scope) {
    // When we have nested any/all quantifiers between a scope & an array/collection field,
    // we want them to align with each other (e.g. "any/any" or "all/all")
    const outerQuantifier = isArray ? getArrayQuantifier(filter) : quantifier;

    return {
      [scope]: {
        [outerQuantifier]: {
          and: [withs, getConditionFilter(filter)],
        },
      },
    };
  }

  return getConditionFilter(filter);
}

const searchField = (
  field: PartnerEndpointQueryKey | string,
  value: string,
) => ({
  [field]: { contains: value },
});

export function getEndpointOdataSearchFilters(searchValue: string) {
  return {
    or: [
      {
        mac_addresses: {
          any: searchField(ITEM_ROOT, searchValue),
        },
      },
      searchField("hostname", searchValue),
      searchField("serial_number", searchValue),
      searchField("cloud_account_id", searchValue),
      searchField("cloud_instance_id", searchValue),
      searchField("serial_number", searchValue),
      {
        instances: {
          any: {
            or: [
              searchField("last_seen", searchValue),
              searchField("platform", searchValue),
              searchField("device_type", searchValue),
              searchField("device_ownership", searchValue),
            ],
          },
        },
      },
    ],
  };
}

export function getUserOdataSearchFilters(searchValue: string) {
  return {
    or: [
      searchField("email", searchValue),
      {
        instances: {
          any: {
            or: [
              searchField("user_type", searchValue),
              searchField("status", searchValue),
              searchField("last_seen", searchValue),
              searchField("risk", searchValue),
            ],
          },
        },
      },
    ],
  };
}

function getConditionFilter<T>(filter: Filter): ODataObjectFilter<T> {
  if (filter.ors && filter.ors.length > 0) {
    const mainCondition = getOperatorFilter(filter);
    const orConditions = filter.ors.map((or) =>
      getOperatorFilter({ ...filter, operator: or.operator, value: or.value }),
    );
    return {
      or: [mainCondition, ...orConditions],
    } as ODataObjectFilter<T>;
  } else {
    return getOperatorFilter(filter);
  }
}

function getOperatorComparator(filter: Filter): object {
  const { operator, value, isArray } = filter;

  switch (operator) {
    case "is not": {
      return { ne: value };
    }
    case "is": {
      return { eq: value };
    }
    case "greater than": {
      return { gt: value };
    }
    case "less than": {
      return { lt: value };
    }
    case "contains": {
      return isArray && typeof value !== "string"
        ? { eq: value }
        : methodCall(filter);
    }
    case "does not contain": {
      return isArray && typeof value !== "string"
        ? { eq: value }
        : methodCall(filter);
    }
    default: {
      return methodCall(filter);
    }
  }
}

function methodCall(filter: Filter) {
  const methodOperator = filter.operator as ODataMethod;
  const method = OPERATOR_ODATA_METHOD[methodOperator];
  if (method) {
    return { [method]: filter.value };
  }
  return {};
}

function getOperatorFilter<T>(filter: Filter): ODataObjectFilter<T> {
  const { id, operator, fn, isArray, value } = filter;

  if (operator === undefined) {
    return {};
  }

  const actualId = isDurationId(id) ? stripDurationId(id) : id;
  const property = fn ? `${fn}(${actualId})` : actualId;
  const comparator = getOperatorComparator(filter);
  const quantifier = isArray
    ? getArrayQuantifier(filter)
    : (filter.quantifier ?? "any");

  const shouldUseArrayStructure =
    isArray &&
    !(value === null && (operator === "is" || operator === "is not"));

  const baseFilter = shouldUseArrayStructure
    ? { [property]: { [quantifier]: { [ITEM_ROOT]: comparator } } }
    : { [property]: comparator };

  return NEGATED_OPERATOR_ODATA_METHOD.has(operator as ODataMethod)
    ? ({ not: [baseFilter] } as ODataObjectFilter<T>)
    : baseFilter;
}

function getArrayQuantifier(filter: Filter): NonNullable<OdataQuantifier> {
  switch (filter.operator) {
    case "is not": {
      return "any";
    }
    case "is": {
      return "all";
    }
    case "contains":
    case "matches": {
      return "any";
    }
    case "does not contain":
    case "does not match": {
      return filter.scope ? "all" : "any";
    }
    default: {
      return "any";
    }
  }
}
